/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useContext, useMemo, useState, useEffect} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import cx from 'classnames'
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import {DateValue, useModal} from '@jutro/components';
import {FormModalPopover, platformMessages} from 'ml-components-platform-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import {useDataRefresh, ViewModelServiceContext} from '@xengage/gw-portals-viewmodel-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import { DocumentDownloadService } from '@xengage/gw-portals-document-js';
import { MLErrorMessageUtil } from 'ml-portals-util-js';
import {PolicyService} from 'gw-capability-gateway';
import { MLGatewayAccountBillingDetailsService } from 'gw-capability-gateway-billing';
import { AccountService } from 'gw-capability-gateway-policy';
import {MLNotificationBlockUtil} from "ml-portals-util-react";
import messages from './MLPolicyDocumentsComponent.messages';
import styles from './MLPolicyDocumentsComponent.module.scss';
import mlResendESignComponentMsg from '../MLResendESignComponent/MLResendESignComponent.messages';
import mlResendESignComponentMetadata from '../MLResendESignComponent/MLResendESignComponent.metadata.json5';
import mlResendESignComponentStyle from '../MLResendESignComponent/MLResendESignComponent.module.scss';

import { Link } from '@jutro/router';

const MLPolicyDocumentsComponent = (props) => {
    const DOCUMENT_READY_STATUS = 'final';
    const DOCUMENT_FAST_TRACK_STATUS = 'fast_track_processing_ext';
    const DOCUMENT_PREVIEW_STATUS = 'Preview_Ext';

    const {
        documents,
        addRemoveDocToShare,
        hidePolicyNumber,
        isUserRetentionAgent,
        isEsign
    } = props;

    const modalApi = useModal();
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { authHeader } = useAuthentication();
    const [order, updateOrder] = useState('desc');
    const orderedDocuments = useMemo(() => _.orderBy(documents, ['dateModified'], [order]), [documents, order])
    const { refreshData } = useDataRefresh();
    //page level error
    let showErrors = false;
    let isButtionDisabled= false;
    //email validation
    let showEmailError = false;
    const [recipientByDocumentId, setRecipientByDocumentId] = useState({});

    const {
        showProgressDialog,
        hideProgressDialog,
        showErrorModal
    } = MLModalService();

    const {
        clearBlockAlerts,
        clearErrorAlerts,
        addErrorAlert,
        addWarningAlert,
        getNumberOfAlerts
    } = MLNotificationBlockUtil();

    const clickOnPolicyNumber = (policyNumber) => {
        MLTagManagerService.sendLinkClickEvent(
            `${MLTagManagerService.getFormName()}_${MLTagManagerService.getStepName()}_Policy Number`,
            policyNumber);
    };

    const newToPortal = (document) => {
        return document.newToPortal_ML || document.newToPortal;
    };

    const actionRequired = (document) => {
        return document.isActionRequired_ML;
    };

    const documentReady = (document) => {
        return document.status === DOCUMENT_READY_STATUS || document.status === DOCUMENT_FAST_TRACK_STATUS || document.status === DOCUMENT_PREVIEW_STATUS;
    }

    const toLocalDate = (date) => {
        return MLLocalDateUtil.serverDateToLocalDate(date);
    };

    const viewDocument = (item) => {
        MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getFormName()}_${MLTagManagerService.getStepName()}_${item.name}`);
        const publicID = item.publicID;
        const sessionID = item.sessionID;
        let xCenterRetrievalURI = null;
        const xCenter = item.xcenter || item.xCenter_ML || item.xcenter_ML;
        switch (xCenter) {
            case 'cc':
                xCenterRetrievalURI = getProxiedServiceUrl('claimDocument');
                break;
            case 'bc':
                xCenterRetrievalURI = getProxiedServiceUrl('billingDocument');
                break;
            case 'pc':
            default:
                xCenterRetrievalURI = getProxiedServiceUrl('policyDocument');
        };
        const documentServicePromise = DocumentDownloadService.getDocumentLink(xCenterRetrievalURI, publicID, sessionID);
        showProgressDialog(platformMessages.downloadingDocument, documentServicePromise);
        documentServicePromise.then(() => {
            if (item.newToPortal || item.newToPortal_ML) {
                if (xCenter === 'bc') {
                    try {
                        if (item.status !== DOCUMENT_FAST_TRACK_STATUS && item.status !== DOCUMENT_PREVIEW_STATUS) {
                            MLGatewayAccountBillingDetailsService.setDocumentAsViewed(publicID, sessionID, authHeader);
                        }
                    } catch (error) {
                         MLErrorMessageUtil.handleException(error, showErrorModal, messages.somethingWentWrong);
                        console.error('Error while updating document viewed in BC', error);
                    }
                } else {
                    try {
                        if (item.status !== DOCUMENT_FAST_TRACK_STATUS && item.status !== DOCUMENT_PREVIEW_STATUS) {
                            PolicyService.setDocumentAsViewed(publicID, sessionID, authHeader);
                        }
                    } catch (error) {
                        MLErrorMessageUtil.handleException(error, showErrorModal, messages.somethingWentWrong);
                        console.error('Error while updating document viewed in PC', error);
                    }
                }
                item.newToPortal_ML = false;
                item.newToPortal = false;
            }
            refreshData();
        }).catch((error) => {
            console.error('Error while trying to view document', error);
             showErrorModal(platformMessages.error, messages.downloadError);
        });
    };

    const getEmptyDescription = () => {
        return translator(messages.documentNotFound)
    };

    const getCheckbox = (document) => {
        if (document.documentSubType_Ext === 'email') {
            return;
        }
        return <input type="checkbox" id={document.publicID} onClick={() => addRemoveDocToShare(document.publicID)} />
    };

    const getPolicyNumber = (document) => {
        return (
            <Link to={`/policies/${document.policyNumber}/summary`}
                onClick={() => clickOnPolicyNumber(document.policyNumber)}>{document.policyNumber}
            </Link>
        );
    };

    const getDateModified = (document) => {
        return <DateValue value={toLocalDate(document.dateModified)} displayFormat='vshort' />;
    };

    const getName = (document) => {
        const textClassName = newToPortal(document) ? undefined : styles.mlViewedDocument
        if (document.documentSubType_Ext === 'email') {
            return (
                <span className={textClassName}>
                    Email - {document.name}
                </span>
            );
        }
        if (documentReady(document)) {
            return (
                <Link onClick={() => viewDocument(document)} textClassName={textClassName}>
                    {document.name}
                </Link>
            );
        }
        return (
            <span className={styles.mlViewedDocument}>
                {document.name}
            </span>
        );
    };

    const getTransactionType = (document) => {
        return (
            <span className={styles.mlViewedDocument}>
                {document.transactionType_Ext}
            </span>
        );
    };

    const getType = (document) => {
        return (
            <span className={styles.mlViewedDocument}>
                {document.mimeTypeDescription_ML || document.mimeTypeDescription }
            </span>
        );
    };

    useEffect(() => {
      if (!isEsign || !documents.length) {
        return;
      }

     const loadRecipients = async () => {
       const entries = await Promise.all(
         documents.map(async (document) => {
           try {
             const groups = await PolicyService.getESignDocsForGroupInviteID(
               document.publicID,
               authHeader,
             );

             const pendingGroup = groups.find(
               (group) => group.stepStatus === "pending",
             );

             const recipient =
               pendingGroup?.inviteStepActions?.[0]?.recipientEmail || "";

             return [document.publicID, recipient];
           } catch (error) {
             console.error("Error loading recipient", error);
             return [document.publicID, ""];
           }
         }),
       );

       setRecipientByDocumentId(Object.fromEntries(entries));
     };

     loadRecipients();
   }, [documents, isEsign, authHeader]);
    
     const formatRecipient = (recipient = "") => {
     if (!recipient.startsWith("sms_signer_")) {
       return recipient;
     }

     return recipient
       .replace("sms_signer_", "")
       .replace("@no.reply", "");
   };

    const getRecipients = (document) => {
        const recipient = recipientByDocumentId[document.publicID] || document.recipientEmail_Ext || "";
     return (
       <span className={styles.mlViewedDocument}>
         {formatRecipient(recipient)}
       </span>
     );
   };
    const emailTrim = ( emailVm, event ) => {
        let emailAddress1 =  event.target.value;
        if(emailAddress1 !== "" && emailAddress1 !== undefined){
            emailAddress1 = emailAddress1.trim();
            event.target.value = emailAddress1;
            _.set(emailVm, 'recipientEmail_Ext.value', emailAddress1);
        }
    }

    const setEmailErrors  = (emailVm, emailAddress1) => {
       _.set(emailVm, 'recipientEmail_Ext.value', ' '+emailAddress1 + ' ');
       showErrors = true;
       isButtionDisabled  =true ;
       showEmailError=true;
       refreshData();
    };

    const validateEmailAddress = (emailVm, docStepStatus) => {
        const esignDoc = emailVm.value;
        //remove space 
        esignDoc.recipientEmail_Ext =esignDoc.recipientEmail_Ext?.trim();
        let emailAddress1 =  esignDoc.recipientEmail_Ext;

        if (emailAddress1){
            emailAddress1 = emailAddress1.trim();
        }

        const isValidEmailEntred =  /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/.test(emailAddress1)

        if (isValidEmailEntred){
            const checkEmailVerified_Ext = AccountService.checkEmailVerified_Ext(
                emailAddress1,
                authHeader
            );

            showProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
            checkEmailVerified_Ext.then(async (res) => {
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);

                if (res === true) {
                    // call update InviteRecipient
                    if (docStepStatus !== "pending") {
                        const updateEmailResponse = await PolicyService.updateResendESignInviteRecipientEmail(esignDoc.publicID, emailAddress1, false, authHeader);
                    }
                    isButtionDisabled = false;
                    showEmailError = false;
                    showErrors = false;
                    clearBlockAlerts('errorInlineNotification');
                    refreshData();
                } else {
                    setEmailErrors(emailVm,emailAddress1);
                }
            }).catch((e) => {
                clearBlockAlerts('errorInlineNotification');
                addWarningAlert(translator(messages.emailServiceIssue), 'emailServiceIssue');
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);

                window.scrollTo(0, 0);
            });
        }
        else{
            setEmailErrors(emailVm,emailAddress1);
        }
    };
    const getIsButtionDisabled =() => {
        if(isButtionDisabled === true){
            return true;
        }
        return false;
    };
    const viewResendModal = async (doc) => {      

        const eSignDocsToSign = await PolicyService.getESignDocsForGroupInviteID(doc.publicID, authHeader);

        const pendingSignatureForms = eSignDocsToSign.find(group => group.stepStatus === "pending");

        const currentStepSignatureForms = pendingSignatureForms?.inviteStepActions.map((form) => ({formName: form.documentName, formRecipient: form.recipientRole}));

        const waitingSignatureForms = eSignDocsToSign.find(group => group.stepStatus !== "pending" && group.stepStatus !== "fulfilled");
        const waitingStepSignatureForms =waitingSignatureForms?.inviteStepActions?.map((form) => ({ waitingRecipientEmail: formatRecipient(form.recipientEmail), waitingFormName: form.documentName, waitingFormRecipient: form.recipientRole})) || [];
        const isWaitingStepSignatureForms = waitingStepSignatureForms && waitingStepSignatureForms.length > 0      
        const documentRecipient = doc.recipientEmail_Ext?.trim();
        const pendingRecipient =pendingSignatureForms?.inviteStepActions?.[0]?.recipientEmail?.trim();
        const rawRecipient = pendingRecipient || documentRecipient || '';
        const isSmsRecipient = rawRecipient.startsWith('sms_signer_');
        const displayRecipient = formatRecipient(rawRecipient);

        const docVM = viewModelService.create(doc, 'pc', 'edge.capabilities.policy.document.dto.PolicyDocumentDTO');  
        _.set(docVM, "recipientPhone_Ext.value", displayRecipient);
        // calling validate email for existing Email.
       if (!isSmsRecipient) {
          validateEmailAddress(docVM, 'pending');
          }
        const resendEmail = async () => {
            // call update and or resend esign invite
            if (isSmsRecipient) {
                try {
                    const phoneUpdatedResendText = await PolicyService.updateResendESignInviteRecipientPhone(doc.publicID, docVM.recipientPhone_Ext.value, true, authHeader);
                } catch (error) {
                    // TODO: Swallow until Business decides on a valid expected behavior
                    showErrorModal(null, messages.somethingWentWrong);
                }                
            } else {
                const emailUpdatedResendEmail = await PolicyService.updateResendESignInviteRecipientEmail(doc.publicID, docVM.recipientEmail_Ext.value, true, authHeader);
            }

           return null

        };

        const componentProps = {
            title: translator(mlResendESignComponentMsg.resendDocuments),
            uiProps: mlResendESignComponentMetadata.componentContent,
            actionBtnLabel: "RESEND",
            disableButton: ()=> getIsButtionDisabled(),
            cancelBtnLabel: mlResendESignComponentMsg.cancelModal,
            modalBodyStyles: mlResendESignComponentStyle,
            viewModel: docVM,
            showValidationErrors: !isSmsRecipient && showErrors,
            editValidateOnOpen: !isSmsRecipient,
            getOverrideProps: () => {
                return {
                    emailAddressInput: {
                        visible: !isSmsRecipient,
                        label: translator(mlResendESignComponentMsg.emailAddress),
                        onBlur:(value, path) => {validateEmailAddress(docVM, "pending")},
                        onFocus: (event) => emailTrim(docVM, event),
                        showValidationErrors: showEmailError,
                        required: true
                    },
                    phoneNumberInput: {
                        label: "Recipient",
                        visible: isSmsRecipient
                    },
                    errorInlineNotification: {
                        visible: showErrors && !isSmsRecipient
                    },
                   
                    currentStepSignatureForms: {
                        label: translator(mlResendESignComponentMsg.documents),
                        data: currentStepSignatureForms
                    },
                    waitingStepHeaderContainer: {
                        visible: isWaitingStepSignatureForms
                    },
                    waitingStepSignatureForms: {
                        data: waitingStepSignatureForms
                    }
                }
            },
            onSaveAction: () => {
                resendEmail()

                return true;
            },
            buttonType: '',
            isFromMLResendEsign:true
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    }

    const getAction = (document) => {
        if (isEsign) {
            return (
                <a className="text-link text-align-left" onClick={() => viewResendModal(document)}
                   target="_blank">{translator(messages.viewDetails)}</a>
            );
        }

        if (documentReady(document) && document.documentSubType_Ext !== 'email') {
            return (
                <a className="text-link text-align-left" onClick={() => viewDocument(document)}
                   target="_blank">{translator(messages.viewPrint)}</a>
            );
        }
        if (document.documentSubType_Ext === 'email') {
            return (
                <div className="disable-text"></div>
            );
        }
        return (
            <div className="disable-text">
                {translator(messages.generating)}
            </div>
        );
    };

    const changeSortOrder =  () => {
        if(order === 'desc'){
            updateOrder('asc')
        }else{
            updateOrder('desc');
        }
    }

    return (
        <table className={cx("table table-striped", styles.mlPolicyDocuments)}>
            <thead>
                <tr>
                    <th />
                    {!hidePolicyNumber && <th>{translator(messages.policyNumber)}</th>}
                    <th className={styles.clickable} onClick={changeSortOrder}>{translator(messages.date)}</th>
                    <th>{isEsign ? translator(messages.transactionType) : translator(messages.documentName)}</th>
                    <th>{isEsign ? translator(messages.recipients) : translator(messages.type)}</th>
                    <th>{translator(messages.actions)}</th>
                </tr>
            </thead>
            <tbody>
                {orderedDocuments.map((document) => {
                    return (
                        <tr>
                            <td className="table-data-collapse">{getCheckbox(document)}</td>
                            {!hidePolicyNumber && <td>{getPolicyNumber(document)}</td>}
                            <td>{getDateModified(document)}</td>
                            <td>{isEsign ? getTransactionType(document) : getName(document)}</td>
                            <td>{isEsign ? getRecipients(document) : getType(document)}</td>
                            <td>{getAction(document)}</td>
                        </tr>
                    )
                })
                }
                {orderedDocuments.length === 0 &&
                    <tr><td colSpan="6">{getEmptyDescription()}</td></tr>
                }
            </tbody>
        </table>
    )
};

MLPolicyDocumentsComponent.propTypes = {
    documents: PropTypes.arrayOf(PropTypes.shape({})),
    addRemoveDocToShare: PropTypes.func.isRequired,
    hidePolicyNumber: PropTypes.bool,
    isUserRetentionAgent: PropTypes.bool
};

MLPolicyDocumentsComponent.defaultProps = {
    documents: [],
    hidePolicyNumber: false,
    isUserRetentionAgent: false
};

export default MLPolicyDocumentsComponent;